/**
 * Custom element that handles disclosure (details/summary) interactions
 * Provides focus management and animation control for collapsible content
 * @extends HTMLElement
 */
class DetailsDisclosure extends HTMLElement {
  /**
   * Creates an instance of DetailsDisclosure
   * Sets up event listeners for focus and toggle events
   */
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle?.querySelector("summary").nextElementSibling;

    this.mainDetailsToggle?.addEventListener(
      "focusout",
      this.onFocusOut.bind(this)
    );
    this.mainDetailsToggle?.addEventListener("toggle", this.onToggle.bind(this));
  }

  /**
   * Handles focusout events to auto-close disclosure when focus leaves
   * Uses setTimeout to allow focus to move to child elements
   */
  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  /**
   * Handles toggle events to control animations when disclosure opens/closes
   * Manages CSS animations on the content element
   */
  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle?.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  /**
   * Opens the disclosure and updates ARIA attributes for accessibility
   */
  open() {
    this.mainDetailsToggle?.setAttribute('open', true);
    this.mainDetailsToggle?.querySelector("summary").setAttribute("aria-expanded", true);
  }

  /**
   * Closes the disclosure and updates ARIA attributes for accessibility
   */
  close() {
    this.mainDetailsToggle?.removeAttribute("open");
    this.mainDetailsToggle?.querySelector("summary").setAttribute("aria-expanded", false);
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

/**
 * Extended DetailsDisclosure for header menu functionality
 * Handles mega menu interactions, submenu navigation, and hover states
 * @extends DetailsDisclosure
 */
class HeaderMenu extends DetailsDisclosure {
  /**
   * Creates an instance of HeaderMenu
   * Initializes header menu specific properties and elements
   */
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
    this.subnavContainer = this.querySelector("[data-subnav-container]");
    this.subnavTriggers = this.querySelectorAll(
      "[data-megamenu-submenu-trigger]"
    );
    this.closeBackgroundSubnavDetails = "";
  }

  /**
   * Called when the element is connected to the DOM
   * Attaches event listeners for menu interactions
   */
  connectedCallback() {
    this.attachEvents();
  }

  /**
   * Attaches mouse event listeners for submenu interactions
   * Handles hover states, menu opening/closing, and delayed closing
   */
  attachEvents() {
    this.subnavTriggers.forEach((subnavTrigger) => {
      subnavTrigger.addEventListener("mouseenter", (event) => {
        this.closeOpenSubnavDetails(subnavTrigger);
        this.openSubnavDetails(subnavTrigger);
      });

      subnavTrigger.addEventListener("mouseleave", (event) => {
        if (event.toElement.closest('[data-subnav-container]') || event.toElement.hasAttribute('data-subnav-container')) return;

        this.closeSubnavDetails(subnavTrigger);
      });
    });

    let closeMenuDelay = null;

    this.mainDetailsToggle?.addEventListener("mouseenter", (e) => {
      clearTimeout(closeMenuDelay);

      this.header.querySelectorAll('header-menu').forEach(headerMenu => {
        headerMenu.close();
      });

      this.open();
    });
    this.mainDetailsToggle?.addEventListener("mouseleave", (e) => {
      closeMenuDelay = setTimeout(() => {
        this.close();
      }, 1000);
    });
  }

  /**
   * Toggles submenu visibility and content
   * @param {HTMLElement} subnavTrigger - The submenu trigger element
   */
  toggleSubnav(subnavTrigger) {
    let targetSubnavItems = "";

    if (subnavTrigger.hasAttribute('open')) {
      this.closeOpenSubnavDetails(subnavTrigger);

      targetSubnavItems = Array.from(
        subnavTrigger
          .querySelector("[data-megamenu-submenu-container]")
          .cloneNode(true).children
      );
    }

    if (this.closeBackgroundSubnavDetails !== subnavTrigger) {
      this.subnavContainer.replaceChildren(...targetSubnavItems);
    } else {
      this.closeBackgroundSubnavDetails = "";
    }
  }

  /**
   * Opens a specific submenu
   * @param {HTMLElement} subnavDetails - The submenu element to open
   */
  openSubnavDetails(subnavDetails) {
    subnavDetails.setAttribute("open", true);

    this.toggleSubnav(subnavDetails);
  }

  /**
   * Closes a specific submenu
   * @param {HTMLElement} subnavDetails - The submenu element to close
   */
  closeSubnavDetails(subnavDetails) {
    subnavDetails.removeAttribute("open");

    this.toggleSubnav(subnavDetails);
  }

  /**
   * Closes all open submenus except the current one
   * @param {HTMLElement} currentSubnavDetails - The current submenu to keep open
   */
  closeOpenSubnavDetails(currentSubnavDetails) {
    const openSubnavDetails = currentSubnavDetails
      .closest(".mega-menu__content")
      .querySelectorAll("[data-megamenu-submenu-trigger][open]");

    openSubnavDetails.forEach((subnavDetails) => {
      if (subnavDetails === currentSubnavDetails) {
        return;
      }

      subnavDetails.removeAttribute("open");
      subnavDetails
        .querySelector("summary")
        .setAttribute("aria-expanded", false);

      this.closeBackgroundSubnavDetails = subnavDetails;
    });
  }

  /**
   * Handles toggle events for the header menu
   * Prevents header hiding when menu is open and sets CSS position variables
   */
  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle?.open;

    if (
      document.documentElement.style.getPropertyValue(
        "--header-bottom-position-desktop"
      ) !== ""
    )
      return;
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define("header-menu", HeaderMenu);
